import type { SellerApplication } from '@modules/seller-onboarding/api';
import type { SellerStaffSection } from './types';

export function isSellerOwner(app: SellerApplication | undefined | null): boolean {
  return app?.cabinetRole !== 'STAFF';
}

export function sellerHasSection(
  app: SellerApplication | undefined | null,
  section: SellerStaffSection,
): boolean {
  if (!app || isSellerOwner(app)) {
    return true;
  }
  return (app.staffSections ?? []).includes(section);
}

export function sellerCanOpenPickupPointsSection(
  app: SellerApplication | undefined | null,
): boolean {
  return sellerHasSection(app, 'CONTACTS') || sellerHasSection(app, 'PRODUCTS');
}

export function sellerCanAccessPickupPointId(
  app: SellerApplication | undefined | null,
  pickupPointId: number,
): boolean {
  if (!app || isSellerOwner(app)) {
    return true;
  }
  const ids = app.staffPickupPointIds ?? [];
  if (ids.length === 0) {
    return true;
  }
  return ids.includes(pickupPointId);
}

export function sellerCanAccessPath(
  app: SellerApplication | undefined | null,
  pathname: string,
): boolean {
  if (!app || isSellerOwner(app)) {
    return true;
  }
  if (pathname === '/' || pathname === '' || pathname.startsWith('/help')) {
    return true;
  }
  if (pathname.startsWith('/support-chat')) {
    return true;
  }
  if (pathname.startsWith('/order-chats')) {
    return true;
  }
  if (
    pathname.startsWith('/products') ||
    pathname.startsWith('/categories')
  ) {
    return sellerHasSection(app, 'PRODUCTS');
  }
  if (pathname.startsWith('/orders')) {
    return sellerHasSection(app, 'ORDERS');
  }
  if (pathname.startsWith('/reviews')) {
    return sellerHasSection(app, 'REVIEWS');
  }
  if (pathname.startsWith('/contacts')) {
    return sellerCanOpenPickupPointsSection(app);
  }
  if (pathname.startsWith('/promotion')) {
    return sellerHasSection(app, 'PROMOTION');
  }
  if (pathname.startsWith('/promo-codes')) {
    return sellerHasSection(app, 'PROMO_CODES');
  }
  if (pathname.startsWith('/bonuses')) {
    return sellerHasSection(app, 'BONUSES');
  }
  if (pathname.startsWith('/finance')) {
    return sellerHasSection(app, 'FINANCE');
  }

  // Только владелец: доступы, профиль, онбординг и служебные разделы.
  const ownerOnlyPrefixes = [
    '/extras',
    '/sales',
    '/accesses',
    '/onboarding',
    '/profile',
    '/tbank-test',
    '/deliveries',
  ];
  if (
    ownerOnlyPrefixes.some(
      (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
    )
  ) {
    return false;
  }

  // Неизвестный путь — пускаем в роутер, там покажется 404.
  return true;
}
